import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Box,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { fetchToursFromUser } from "../../store/Vanlifer/tourSlice";
import { TourOverview } from "../../services/restserver-openapi";
import { asyncGetMostLikedTours } from "../../store/Vanlifer/Community/profileTourSlice";
import TourGrid from "./Community/TourGrid";
import { getFuturePlannedToursCount } from "../../utils/Vanlifer/tour";

import { formatAsSwissDate } from "../../utils/dateUtil";
import imgSlider1 from "../../assets/Images/trip_planner.jpg";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";

import PublicOffIcon from "@mui/icons-material/PublicOff";
import PublicIcon from "@mui/icons-material/Public";
import PersonIcon from "@mui/icons-material/Person";

interface BookingOverviewProps {
    userSpecificationId: number;
    display?: "compact" | "detailed";
}

type FilterKey = "FUTURE" | "ONGOING" | "PAST" | "CANCELED" | "ALL" | "PUBLIC" | "LIKED";

const formatSwiss = (ts?: number) => (ts ? formatAsSwissDate(ts) : "Unbekannt");

const RecordingDot = () => (
    <Box
        component="span"
        sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: "success.main",
            display: "inline-block",
            position: "relative",
            animation: "blink 1s infinite",

            // optionaler Ping-Ring
            "&::after": {
                content: '""',
                position: "absolute",
                inset: -3,
                borderRadius: "50%",
                border: "2px solid",
                borderColor: "success.main",
                opacity: 0.6,
                animation: "ping 1.2s ease-out infinite",
            },

            "@keyframes blink": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.25 },
            },
            "@keyframes ping": {
                "0%": { transform: "scale(0.8)", opacity: 0.6 },
                "80%": { transform: "scale(1.8)", opacity: 0 },
                "100%": { transform: "scale(1.8)", opacity: 0 },
            },
        }}
    />
);

const getStatusLabel = (tour: TourOverview, opts?: { isOngoing?: boolean }) => {
    if (tour.status === "CANCELED") return "Gelöscht";
    if (tour.tourCompleted) return "Abgeschlossen";
    if (opts?.isOngoing) return "On Tour";
    if (tour.status === "BOOKED") return "Geplant";
    return "Unbekannt";
};

const getStatusIcon = (tour: TourOverview, opts?: { isOngoing?: boolean }) => {
    if (tour.status === "CANCELED") {
        return (
            <Tooltip title="Gelöscht">
                <CancelIcon color="error" fontSize="small" />
            </Tooltip>
        );
    }
    if (tour.tourCompleted) {
        return (
            <Tooltip title="Abgeschlossen">
                <CheckCircleIcon color="success" fontSize="small" />
            </Tooltip>
        );
    }
    if (tour.status === "BOOKED") {
        if (opts?.isOngoing) {
            return (
                <Tooltip title="Aufzeichnung läuft">
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <RecordingDot />
                    </Box>
                </Tooltip>
            );
        }

        return (
            <Tooltip title="Geplant">
                <ScheduleIcon color="inherit" fontSize="small" />
            </Tooltip>
        );
    }
    return (
        <Tooltip title="Unbekannter Status">
            <HelpOutlineIcon color="disabled" fontSize="small" />
        </Tooltip>
    );
};

/**
 * Abgeleitet von TourCardPublic – Owner-Variante:
 */
function TourCardOwner(props: {
    tour: TourOverview;
    showOwner?: boolean;
    isOwner?: boolean;
    showEditFab?: boolean;
}) {
    const { tour, showOwner = true, isOwner = true } = props;

    const { fromDate, toDate } =
    tour.stays?.reduce(
        (acc, stay) => ({
            fromDate: Math.min(acc.fromDate ?? stay.fromDate ?? Infinity, stay.fromDate ?? Infinity),
            toDate: Math.max(acc.toDate ?? stay.toDate ?? 0, stay.toDate ?? 0),
        }),
        {} as { fromDate?: number; toDate?: number }
    ) || {};

    const rightImage =
        tour.sasDownloadLink !== "" && tour.sasDownloadLink != null ? tour.sasDownloadLink : imgSlider1.toString();

    const bookedAt = tour.statusDate ? formatSwiss(tour.statusDate) : "Kein Datum";
    const priceLabel = tour.price != null ? `${tour.price.toFixed(2)} CHF` : "Keine Information";
    const now = Date.now();
    const hasStart = typeof fromDate === "number" && Number.isFinite(fromDate);
    const hasEnd = typeof toDate === "number" && Number.isFinite(toDate) && toDate > 0;

    const isOngoing =
        tour.status === "BOOKED" &&
        !tour.tourCompleted &&
        hasStart &&
        fromDate! <= now &&
        (!hasEnd || now <= toDate!);

    return (
        <Card
            elevation={0}
            sx={{
                position: "relative",
                height: 320,
                color: "white",
                textDecoration: "none",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: 2,
                border: 0,
                boxShadow: "none",

                "&:hover img": {
                    transform: "scale(1.05)",
                },
                "&:hover": {
                    backgroundColor: "transparent",
                },
            }}
        >
            {/* Status (oben links) */}
            <Box
                sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: "rgba(0,0,0,0.35)",
                    backdropFilter: "blur(6px)",
                }}
            >
                {getStatusIcon(tour, { isOngoing })}
                <Typography variant="caption" sx={{ fontWeight: 800, color: "white" }}>
                    {getStatusLabel(tour, { isOngoing })}
                </Typography>
            </Box>

            {/* Link content */}
            <Box
                component={RouterLink}
                to={`/vanlifer/Tour/${tour.tourId}`}
                sx={{
                    position: "relative",
                    zIndex: 1,
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    textDecoration: "none",
                    color: "inherit",
                }}
            >
                {/* Background image */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                        zIndex: 0,
                    }}
                >
                    <Box
                        component="img"
                        src={rightImage}
                        alt="Tour background"
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            transition: "transform 0.4s ease",
                        }}
                    />
                </Box>

                {/* Gradient overlay */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background:
                            "linear-gradient(to bottom, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.26) 40%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.28) 70%, rgba(0,0,0,0.62) 100%)",
                        zIndex: 1,
                    }}
                />

                <CardContent
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "100%",
                        width: "100%",
                        p: 2,
                    }}
                >
                    {/* Top */}
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
                            {tour.sharingMode === "PRIVATE" ? (
                                <Tooltip title="Private Tour">
                                    <PublicOffIcon fontSize="small" sx={{ opacity: 0.9 }} />
                                </Tooltip>
                            ) : (
                                <Tooltip title="Öffentliche Tour">
                                    <PublicIcon fontSize="small" sx={{ opacity: 0.9 }} />
                                </Tooltip>
                            )}

                            <Typography
                                variant="h5"
                                sx={{
                                    mb: 0,
                                    fontWeight: 900,
                                    lineHeight: 1.15,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                }}
                            >
                                {tour.name || "Ohne Name"}
                            </Typography>
                        </Box>

                        <Typography variant="caption" sx={{ display: "block", mt: 0.75, opacity: 0.95 }}>
                            {fromDate && toDate ? `${formatSwiss(fromDate)} – ${formatSwiss(toDate)}` : "Reisedaten unbekannt"}
                        </Typography>

                        {showOwner && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75, opacity: 0.95 }}>
                                <Typography variant="caption">{tour.owner ?? "Unbekannter User"}</Typography>
                                {isOwner && (
                                    <Tooltip title="Deine Tour">
                                        <PersonIcon fontSize="inherit" />
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* Middle */}
                    <Typography
                        variant="body2"
                        sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "normal",
                            mt: 1,
                            opacity: 0.98,
                        }}
                    >
                        {tour.description || "—"}
                    </Typography>

                    {/* Bottom (zusätzliche Infos) */}
                    <Box sx={{ mt: 1 }}>
                        <Divider sx={{ borderColor: "rgba(255,255,255,0.25)", mb: 1 }} />

                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                            <Typography variant="caption" sx={{ opacity: 0.95 }}>
                                Geplant am: <b>{bookedAt}</b>
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.95 }}>
                                Preis: <b>{priceLabel}</b>
                            </Typography>
                        </Box>

                        <Typography variant="caption" sx={{ display: "block", mt: 0.25, opacity: 0.85 }}>
                            Tour ID: {tour.tourId ?? "—"}
                        </Typography>
                    </Box>
                </CardContent>
            </Box>
        </Card>
    );
}

function TourCardSkeleton() {
    return (
        <Card
            elevation={0}
            sx={{
                height: 320,
                borderRadius: 2,
                border: 0,
                boxShadow: "none",
                overflow: "hidden",
            }}
        >
            <Skeleton variant="rectangular" width="100%" height="100%" />
        </Card>
    );
}

function CardGridSkeleton() {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr" },
                gap: 2,
            }}
        >
            {Array.from({ length: 6 }).map((_, i) => (
                <TourCardSkeleton key={i} />
            ))}
        </Box>
    );
}

export default function BookingOverview({ userSpecificationId, display = "detailed" }: BookingOverviewProps) {
    const dispatch = useAppDispatch();
    const userEntitlements = useAppSelector((state) => state.user.entitlements);
    const communityName = useAppSelector((state) => state.user.user.communityName);
    const likedTours = useAppSelector((state) => state.profileTours.likedTours);
    const likedToursLoading = useAppSelector((state) => state.profileTours.loading);
    const likedToursError = useAppSelector((state) => state.profileTours.error);

    const [futureTours, setFutureTours] = useState<TourOverview[] | null>(null);
    const [ongoingTours, setOngoingTours] = useState<TourOverview[] | null>(null);
    const [pastTours, setPastTours] = useState<TourOverview[] | null>(null);
    const [cancelTours, setCancelTours] = useState<TourOverview[] | null>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [plannedToursCount, setPlannedToursCount] = useState(0);

    const [filter, setFilter] = useState<FilterKey>("FUTURE");

    const getFirstStartDate = useCallback((tour: TourOverview): number | undefined => {
        const stays = tour.stays;
        if (stays && stays.length > 0) {
            const sorted = stays.slice().sort((a, b) => (a.fromDate || 0) - (b.fromDate || 0));
            return sorted[0]?.fromDate;
        }
        return undefined;
    }, []);

    const getLastEndDate = useCallback((tour: TourOverview): number | undefined => {
        const stays = tour.stays;
        if (stays && stays.length > 0) {
            const sorted = stays.slice().sort((a, b) => (b.toDate || 0) - (a.toDate || 0));
            return sorted[0]?.toDate;
        }
        return undefined;
    }, []);

    const isPastByDate = useCallback((tour: TourOverview, now: number): boolean => {
        const end = getLastEndDate(tour);
        return typeof end === "number" && end < now;
    }, [getLastEndDate]);

    const orderByFirstStartDate = useCallback(
        (tours: TourOverview[]) =>
            tours.slice().sort((a, b) => {
                const dateA = getFirstStartDate(a);
                const dateB = getFirstStartDate(b);
                if (dateA === undefined && dateB === undefined) return 0;
                if (dateA === undefined) return 1;
                if (dateB === undefined) return -1;
                return dateA - dateB;
            }),
        [getFirstStartDate]
    );

    const fetchTours = useCallback(async () => {
        setIsLoading(true);
        setIsError(false);

        try {
            const [booked, cancelled] = await Promise.all([
                dispatch(fetchToursFromUser({ vanliferId: userSpecificationId, tourStatus: "BOOKED" })).unwrap(),
                dispatch(fetchToursFromUser({ vanliferId: userSpecificationId, tourStatus: "CANCELED" })).unwrap(),
            ]);

            const now = Date.now();
            const bookedList: TourOverview[] = booked ?? [];
            const cancelledList: TourOverview[] = cancelled ?? [];

            const ongoing = bookedList.filter((t) => {
                if (t.tourCompleted || isPastByDate(t, now)) return false;
                const start = getFirstStartDate(t);
                if (!start) return false;
                const end = getLastEndDate(t);
                if (end) return start <= now && now <= end;
                return start <= now;
            });

            const completed = bookedList.filter((t) => !!t.tourCompleted || isPastByDate(t, now));

            const future = bookedList.filter((t) => {
                if (t.tourCompleted || isPastByDate(t, now)) return false;
                return !ongoing.some((ot) => ot.tourId === t.tourId);
            });

            setOngoingTours(orderByFirstStartDate(ongoing));
            setFutureTours(orderByFirstStartDate(future));
            setPastTours(orderByFirstStartDate(completed));
            setCancelTours(orderByFirstStartDate(cancelledList));
        } catch (e) {
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, getFirstStartDate, getLastEndDate, isPastByDate, orderByFirstStartDate, userSpecificationId]);

    useEffect(() => {
        fetchTours();
    }, [fetchTours]);

    useEffect(() => {
        const loadFuturePlannedToursCount = async () => {
            try {
                const count = await getFuturePlannedToursCount();
                setPlannedToursCount(count);
            } catch {
                setPlannedToursCount(0);
            }
        };

        loadFuturePlannedToursCount();
    }, []);

    useEffect(() => {
        if (communityName) {
            dispatch(asyncGetMostLikedTours(communityName));
        }
    }, [communityName, dispatch]);

    const allTours = useMemo(() => {
        const merged = [
            ...(futureTours ?? []),
            ...(ongoingTours ?? []),
            ...(pastTours ?? []),
            ...(cancelTours ?? []),
        ];
        return orderByFirstStartDate(merged);
    }, [cancelTours, futureTours, ongoingTours, pastTours, orderByFirstStartDate]);

    const publicTours = useMemo(() => {
        // "PUBLIC" = nicht PRIVATE (bei deinen eigenen Touren)
        return allTours.filter((t) => t.sharingMode !== "PRIVATE");
    }, [allTours]);

    const counts = useMemo(
        () => ({
            FUTURE: futureTours?.length ?? 0,
            ONGOING: ongoingTours?.length ?? 0,
            PAST: pastTours?.length ?? 0,
            CANCELED: cancelTours?.length ?? 0,
            ALL: allTours.length,
            PUBLIC: publicTours.length,
            LIKED: likedTours?.length ?? 0,
        }),
        [
            allTours.length,
            cancelTours?.length,
            futureTours?.length,
            likedTours?.length,
            ongoingTours?.length,
            pastTours?.length,
            publicTours.length,
        ]
    );

    const filteredTours: TourOverview[] = useMemo(() => {
        switch (filter) {
            case "FUTURE":
                return futureTours ?? [];
            case "ONGOING":
                return ongoingTours ?? [];
            case "PAST":
                return pastTours ?? [];
            case "CANCELED":
                return cancelTours ?? [];
            case "PUBLIC":
                return publicTours;
            case "LIKED":
                return likedTours ?? [];
            case "ALL":
            default:
                return allTours;
        }
    }, [allTours, cancelTours, filter, futureTours, likedTours, ongoingTours, pastTours, publicTours]);

    const maxPlannedTours = useMemo(() => {
        const rawValue = userEntitlements.find((entitlement) => entitlement.featureId === "vanlifermaxnrofplannedtours")?.value;
        if (!rawValue || rawValue.toLowerCase() === "unlimited") {
            return null;
        }

        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : null;
    }, [userEntitlements]);

    const plannedToursLimitReached = maxPlannedTours !== null && plannedToursCount >= maxPlannedTours;

    const ActionRow = (props: {
        icon: React.ReactNode;
        title: string;
        subtitle?: string;
        active?: boolean;
        disabled?: boolean;
        tooltip?: string;
        count?: number;
        onClick?: () => void;
    }) => {
        const { icon, title, subtitle, active, disabled, tooltip, count, onClick } = props;

        const content = (
            <ListItem disablePadding>
                <ListItemButton
                    disabled={disabled}
                    onClick={onClick}
                    sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        gap: 0.5,
                        "&.Mui-disabled": { opacity: 0.55 },
                        ...(active
                            ? {
                                bgcolor: "action.selected",
                                "&:hover": { bgcolor: "action.selected" },
                            }
                            : {}),
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: active ? "primary.main" : "text.secondary" }}>
                        {icon}
                    </ListItemIcon>

                    <ListItemText
                        primary={
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                <Typography sx={{ fontWeight: 800 }} noWrap>
                                    {title}
                                </Typography>

                                {typeof count === "number" && (
                                    <Badge
                                        badgeContent={count}
                                        color={active ? "primary" : "default"}
                                        sx={{ "& .MuiBadge-badge": { fontWeight: 900 } }}
                                    />
                                )}
                            </Box>
                        }
                        secondary={subtitle}
                        secondaryTypographyProps={{ color: "text.secondary" }}
                    />
                </ListItemButton>
            </ListItem>
        );

        return tooltip ? (
            <Tooltip title={tooltip}>
                <Box component="span">{content}</Box>
            </Tooltip>
        ) : (
            content
        );
    };

    const renderGrid = () => {
        if (filter === "LIKED") {
            if (likedToursError) return <Typography color="error">Fehler beim Laden.</Typography>;
            return <TourGrid loading={likedToursLoading} tours={likedTours} grouped={false} showOwner={true} />;
        }

        if (isLoading) return <CardGridSkeleton />;
        if (isError) return <Typography color="error">Fehler beim Laden.</Typography>;
        if (!filteredTours || filteredTours.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    Keine Reisen vorhanden.
                </Typography>
            );
        }

        return (
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr" },
                    gap: 2,
                }}
            >
                {filteredTours.map((tour) => (
                    <TourCardOwner
                        key={tour.tourId}
                        tour={tour}
                        showOwner={filter === "PUBLIC"}
                        isOwner
                        showEditFab={filter !== "CANCELED"}
                    />
                ))}
            </Box>
        );
    };

    const renderCompactTable = () => {
        if (isLoading) return <CardGridSkeleton />;
        if (isError) return <Typography color="error">Fehler beim Laden.</Typography>;
        if (!allTours || allTours.length === 0) return <Typography>Keine Reisen vorhanden.</Typography>;

        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    borderRadius: 4,
                    width: "100%",
                    border: 0,
                    boxShadow: "none",
                    bgcolor: "background.paper",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, mb: 2 }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 900 }}>
                            Tourübersicht
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Kompakte Tabellenansicht
                        </Typography>
                    </Box>
                </Box>

                {plannedToursLimitReached && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Du hast das Limit für geplante Reisen erreicht ({plannedToursCount}/{maxPlannedTours}).
                        Bitte lösche eine zukünftige Reise, bevor du eine weitere planst.
                    </Alert>
                )}

                <Box sx={{ overflowX: "auto" }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {["Tourname", "Status", "Startdatum", "Enddatum"].map((h) => (
                                    <TableCell key={h} sx={{ bgcolor: "background.paper" }}>
                                        <Typography
                                            color="text.secondary"
                                            variant="caption"
                                            fontWeight={800}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            {h}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {allTours.map((tour) => {
                                const startDate = getFirstStartDate(tour);
                                const endDate = getLastEndDate(tour);
                                const now = Date.now();
                                const isOngoing =
                                    tour.status === "BOOKED" &&
                                    !tour.tourCompleted &&
                                    typeof startDate === "number" &&
                                    startDate <= now &&
                                    (!endDate || now <= endDate);

                                return (
                                    <TableRow key={tour.tourId} hover sx={{ "& td": { borderBottomColor: "divider" } }}>
                                        <TableCell sx={{ fontWeight: 700 }}>{tour.name}</TableCell>

                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {getStatusIcon(tour, { isOngoing })}
                                                <Typography variant="body2">{getStatusLabel(tour, { isOngoing })}</Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell>{formatSwiss(startDate)}</TableCell>
                                        <TableCell>{formatSwiss(endDate)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>
        );
    };

    if (display === "compact") {
        return <Box>{renderCompactTable()}</Box>;
    }

    return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 2 }}>
            {/* LEFT: Actions */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 4,
                    p: 2,
                    height: "fit-content",
                    border: 0,
                    boxShadow: "none",
                    bgcolor: "background.paper",
                }}
            >
                <List sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <ActionRow
                        icon={<ViewListRoundedIcon />}
                        title="Alle Reisen"
                        subtitle="Alles auf einen Blick"
                        active={filter === "ALL"}
                        count={counts.ALL}
                        onClick={() => setFilter("ALL")}
                    />
                    <ActionRow
                        icon={<ScheduleIcon />}
                        title="Bevorstehend"
                        subtitle="Noch nicht gestartet"
                        active={filter === "FUTURE"}
                        count={counts.FUTURE}
                        onClick={() => setFilter("FUTURE")}
                    />
                    <ActionRow
                        icon={<TodayRoundedIcon />}
                        title="Aktuell"
                        subtitle="Läuft gerade"
                        active={filter === "ONGOING"}
                        count={counts.ONGOING}
                        onClick={() => setFilter("ONGOING")}
                    />
                    <ActionRow
                        icon={<HistoryRoundedIcon />}
                        title="Vergangen"
                        subtitle="Abgeschlossen"
                        active={filter === "PAST"}
                        count={counts.PAST}
                        onClick={() => setFilter("PAST")}
                    />
                    <ActionRow
                        icon={<CancelRoundedIcon />}
                        title="Gelöscht"
                        subtitle="Abgebrochen / gelöscht"
                        active={filter === "CANCELED"}
                        count={counts.CANCELED}
                        onClick={() => setFilter("CANCELED")}
                    />

                    <Divider sx={{ my: 1.25 }} />

                    <ActionRow
                        icon={<PublicRoundedIcon />}
                        title="Öffentlich"
                        subtitle="Deine öffentlichen Touren"
                        active={filter === "PUBLIC"}
                        count={counts.PUBLIC}
                        onClick={() => setFilter("PUBLIC")}
                    />
                    <ActionRow
                        icon={<FavoriteRoundedIcon />}
                        title="Gelikte Reisen"
                        subtitle="Favorisierte Reisen aus der Community"
                        active={filter === "LIKED"}
                        count={counts.LIKED}
                        onClick={() => setFilter("LIKED")}
                    />
                </List>
            </Paper>

            {/* RIGHT: Content */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 4,
                    p: 2,
                    border: 0,
                    boxShadow: "none",
                    bgcolor: "background.paper",
                }}
            >
                {plannedToursLimitReached && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Du hast das Limit für geplante Reisen erreicht ({plannedToursCount}/{maxPlannedTours}).
                        Bitte lösche eine zukünftige Reise, bevor du eine weitere planst.
                    </Alert>
                )}
                {renderGrid()}
            </Paper>
        </Box>
    );
}
